/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component} from 'react';

import {Link, withRouter} from "react-router-dom";
import Loader from "react-loader-spinner";

import AppNavbar from "./../components/AppNavbar";
import AppFooter from "../components/AppFooter";
import * as DefaultOffers from "./SubscribeDefaultOffers.json";
import {getLinkForSignIn, isUserSignedIn, UserContext} from "../UserContext";

import {
    intervalIdFromParams,
    isSamePlan,
    FING_PLAN_FREE,
    accountTypePrettyName,
    INTERVAL_MONTH,
    INTERVAL_YEAR,
    promoCodeFromParams,
    productIdFromParams,
    productPrettyName,
    matchesCaseInsensitive,
    enrichOfferOptions, mobilePlatformPrettyName, FING_PLAN_PROFESSIONAL,
} from "../helpers/BillingHelper";
import OptionBar from "./OptionBar";
import {failedToast, successToast} from "../helpers/ToastHelper";
import PricingTier from "./PricingTier";
import {ToastContainer} from "react-toastify";
import ToolsImg from "../assets/svg/stories/premium-tools.png";
import MobileActiveImg from "../assets/svg/stories/migrate-subscription.svg";


class SubscribeCart extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);
        this.state = {
            offers: [],
            subscription: undefined,
            mobileSubscription: undefined,
            showConfirmationDialog : false,
            updateSubscriptionInProgress: false,
            manageInProgress: false
        };
    }

    componentDidMount() {
        this.fetchOffers();
        window.scrollTo(0, 0)
        document.title = "Fing - Choose your subscription";
    }

    fetchOffers() {
        const query = new URLSearchParams(this.props.location.search);
        const promoCode = promoCodeFromParams(query);
        const promoParam = promoCode ? `?promo_code=${promoCode}` : "";
        fetch(`/rest/subscription/offers${promoParam}`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(result => {
                if (result && result.options) {
                    enrichOfferOptions(result.options);
                }
                const webSubscription = result && result.currentWebSubscription ? result.currentWebSubscription : undefined;
                const mobileSubscription = result && result.currentMobileSubscription ? result.currentMobileSubscription : undefined;
                this.setState({offers: result, subscription: webSubscription, mobileSubscription: mobileSubscription});
            })
            .catch(e => {
                let defaultOffers = { options: DefaultOffers.options };
                enrichOfferOptions(defaultOffers.options);
                this.setState({offers: defaultOffers});
            });
    }

    updateSubscription(productId, intervalId, discountOffer, callback) {
        const checkoutParams = new URLSearchParams();
        if (productId) checkoutParams.set("product", productId.toLowerCase());
        if (intervalId) checkoutParams.set("interval", intervalId.toLowerCase());
        if (discountOffer) checkoutParams.set("discount_offer", discountOffer);

        fetch(`/rest/subscription/update?${checkoutParams.toString()}`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then((response) => {
                if (!(response && response.planId)) {
                    callback();
                    return;
                }

                this.setState({subscription: response});
                callback(response);

                // Refresh the user profile if needed
                this.fetchProfile(this.context).then(data => {
                    if (data && data.id && data.accountType) {
                        const newContext = Object.assign({}, this.context);
                        newContext.userProfile.accountType = response.accountType;
                        newContext.userContextUpdater(newContext);
                    }
                });
            })
            .catch(e => callback(null, e))
        ;
    }

    fetchProfile(userContext) {
        return fetch('/rest/users/profile')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json());
    }

    fetchPortalUrl(callback) {
        this.setState({manageInProgress: true})
        return fetch('/rest/subscription/manage')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(response => {
                if (response && response.portalSessionUrl) {
                    callback(response.portalSessionUrl); // Leave the "progress" active, until the screen changes
                    return;
                }
                this.setState({manageInProgress: false});
            })
            .catch(ignore => {
                this.setState({manageInProgress: false});
            });
    }

    validateRestResponse(response) {
        if (response.status === 404) {
            throw Error("Not found");
        } else if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    normalizeURLSearchParams() {
        const params = new URLSearchParams(this.props.location.search);
        const productId = productIdFromParams(params);
        const intervalId = intervalIdFromParams(params);
        const promoCode = promoCodeFromParams(params);

        if (productId) params.set("product", productId.toLowerCase());
        if (intervalId) params.set("interval", intervalId.toLowerCase());
        if (promoCode) params.set("promo_code", promoCode);

        return params;
    }

    // ============================================================================================================
    //  RENDERING
    // ============================================================================================================

    render() {
        let userContext = this.context;
        return (
            <div style={{backgroundColor: "#f8f8f8"}}>
                <AppNavbar mode='account'/>
                <main id="content" role="main">
                    {this.renderMainContent(userContext)}
                </main>
                <AppFooter/>
            </div>
        );
    }

    renderMainContent(userContext) {
        const {offers, subscription, mobileSubscription} = this.state;
        const userProfile = userContext.userProfile;

        if (!isUserSignedIn(userContext)) {
            return this.renderSignedOutUser(userContext, offers);
        }

        if (subscription) {
            return this.renderActiveWebSubscription(userProfile, offers, subscription);
        }

        if (mobileSubscription) {
            return this.renderActiveMobileSubscription(userProfile, offers, mobileSubscription);
        }

        if (isSamePlan(userProfile.accountType, FING_PLAN_FREE)) {
            return this.renderFreeUser(userProfile, offers);
        }

        if (offers && offers.options && offers.options.length > 0) {
            return this.renderManuallySetUser(userProfile);
        }
    }

    renderSignedOutUser(userContext, offers) {
        const hasOffers = offers && offers.options && offers.options.length > 0;
        const urlParams = this.normalizeURLSearchParams();

        return <div className="container space-1">
            <h1 className="h2 font-weight-bolder mb-0">Sign in to discover what we have in store for you</h1>
            <p className="lead mb-6">Unlock new offers with added perks, just for you.</p>
            {hasOffers ?
                <div className="mx-auto">
                    {this.renderPlanOptions(urlParams, offers.options)}
                    {this.renderSignInButton(userContext)}
                </div> : this.renderPlansLoading()
            }
        </div>;
    }

    renderFreeUser(userProfile, offers) {
        const hasOffers = offers && offers.options && offers.options.length > 0;
        const urlParams = this.normalizeURLSearchParams();
        const productId = productIdFromParams(urlParams);
        const intervalId = intervalIdFromParams(urlParams);
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;
        const selectedOffer = hasOffers ? offers.options.find(offer =>
            matchesCaseInsensitive(offer.billingCycle, intervalId) &&
            matchesCaseInsensitive(offer.productId, productId)) : null;

        return <div className="container space-1">
            <h1 className="h2 font-weight-bolder mb-0">{name}, start securing your network today</h1>
            <p className="lead mb-6">The best deals have been applied for you.</p>
            {hasOffers ?
                <div className="mx-auto">
                    {this.renderPlanOptions(urlParams, offers.options)}
                    {this.renderCheckoutButton(urlParams, selectedOffer)}
                </div> : this.renderPlansLoading()
            }
        </div>;
    }

    renderActiveMobileSubscription(userProfile, offers, mobileSubscription) {
        const accountType = userProfile.accountType;
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;

        const hasOffers = offers && offers.options && offers.options.length > 0;
        const urlParams = this.normalizeURLSearchParams();
        const productId = FING_PLAN_PROFESSIONAL; // Force the professional plan
        // const productId = productIdFromParams(urlParams);
        const intervalId = intervalIdFromParams(urlParams);
        const selectedOffer = hasOffers ? offers.options.find(offer =>
            matchesCaseInsensitive(offer.billingCycle, intervalId) &&
            matchesCaseInsensitive(offer.productId, productId)) : null;

        return <div className="container space-1">
            <h1 className="h2 font-weight-bolder mb-0">{name}, migrate your active <span className="text-highlight-warning">{accountTypePrettyName(accountType)}</span> subscription</h1>
            <p className="lead mb-6">{mobilePlatformPrettyName(mobileSubscription.platform)} subscriptions can be transferred to a Professional account, with up to 80% of your mobile purchase credited back.</p>
            <div className="mx-auto">
                <figure className="text-center mb-6">
                    <img className="w-50"
                         src={MobileActiveImg}
                         alt={"Tools"} />
                </figure>
                {mobileSubscription.migratedTo ? this.renderViewDashboardButton() : this.renderMigrateButtons(urlParams, selectedOffer)}
            </div>
        </div>;
    }

    renderActiveWebSubscription(userProfile, offers, subscription) {
        const hasOffers = offers && offers.options && offers.options.length > 0;
        const urlParams = this.normalizeURLSearchParams();
        const productId = productIdFromParams(urlParams);
        const intervalId = intervalIdFromParams(urlParams);
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;
        const selectedOffer = hasOffers ? offers.options.find(offer =>
            matchesCaseInsensitive(offer.billingCycle, intervalId) &&
            matchesCaseInsensitive(offer.productId, productId)) : null;

        return <div className="container space-1">
            <h1 className="h2 font-weight-bolder mb-0">{name}, choose the plan that works best for you</h1>
            <p className="lead mb-6">The best deals have been applied for you.</p>
            {hasOffers ?
                <div className="mx-auto">
                    {this.renderPlanOptions(urlParams, offers.options, subscription)}
                    {this.renderUpdateButton(urlParams, selectedOffer, subscription)}
                </div> : this.renderPlansLoading()
            }
            {this.renderConfirmChangeDialog(selectedOffer)}
            <ToastContainer />
        </div>;
    }

    renderManuallySetUser(userProfile) {
        const accountType = userProfile.accountType;
        const name = userProfile.firstName ? userProfile.firstName : userProfile.name;

        return <div className="container space-1">
            <h1 className="h2 font-weight-bolder mb-0">{name}, you already have an active <span className="text-highlight-warning">{accountTypePrettyName(accountType)}</span> subscription</h1>
            <p className="lead mb-6">No purchase transaction was found. Please contact support if you believe there should be one.</p>
            <div className="mx-auto">
                <figure className="text-center mb-6">
                    <img className="img-fluid img-fluid"
                         src={ToolsImg}
                         alt={"Tools"} />
                </figure>
                {this.renderViewDashboardButton()}
            </div>
        </div>;
    }

    // ============================================================================================================

    renderSignInButton(userContext) {
        return <div className="max-w-50rem mx-auto">
                <a className="btn btn-primary btn-sm w-100"
                      href={getLinkForSignIn(userContext, window.location.href)}>Sign in to continue</a>
        </div>;
    }

    renderMigrateButtons(urlParams, offer) {
        let buttonParams = urlParams;
        if (offer.offerId) {
            buttonParams = new URLSearchParams(urlParams);
            buttonParams.set("discount_offer", offer.offerId);
        }

        return <div className="max-w-50rem mx-auto">
            <Link className="btn btn-primary btn-sm w-100 mb-3"
                  to={`/account/subscribe/embedded-checkout?${buttonParams.toString()}`}><span
                className="fas fa-lock mr-3"/>Migrate to Professional account
            </Link>

            <Link className="btn btn-outline-primary btn-sm w-100"
                  to="/account/dashboard">View account dashboard</Link>
        </div>;
    }
    renderViewDashboardButton() {
        return <div className="max-w-50rem mx-auto">
            <Link className="btn btn-primary btn-sm w-100"
                  to="/account/dashboard">View account dashboard</Link>
        </div>;
    }

    renderCheckoutButton(urlParams, offer) {
        // const trialText = offer.trialDays > 0 ? "Free trial for " + offer.trialDays + " days" : null;
        // const discountText = offer.offerAmount > 0 ?
        //     currencySymbol(offer.currency) + offer.offerAmount + " first " +
        //     (offer.offerDuration > 1 ? offer.offerDuration + " " + offer.billingCycle + "s" : offer.billingCycle) : null
        // const priceText = currencySymbol(offer.currency) + offer.standardAmount + " billed every " + offer.billingCycle;

        let buttonParams = urlParams;
        if (offer.offerId) {
            buttonParams = new URLSearchParams(urlParams);
            buttonParams.set("discount_offer", offer.offerId);
        }

        const offerHasTrial = offer.trialDays > 0;
        const canPurchaseAgents = offer.productId && offer.productId.toUpperCase() === FING_PLAN_PROFESSIONAL;
        return <div className="max-w-50rem mx-auto">
            <Link className="btn btn-primary btn-sm w-100"
                  to={`/account/subscribe/embedded-checkout?${buttonParams.toString()}`}><span
                className="fas fa-lock mr-3"/>{offerHasTrial ? "Start your free trial" : "Continue to checkout"}
            </Link>
            <div className="pt-2">
                <p>
                    {offerHasTrial && <span>You will pay nothing today for this offer. </span>}
                    {!canPurchaseAgents && <>You can review your order details in the checkout page before finalizing your purchase.</>}
                    {canPurchaseAgents && <p>Additional monitored networks available upon request.
                        Please contact us at business at fing.com after purchasing the Professional plan to add more networks.</p>}
                </p>
{/*
                <p>{trialText && trialText + ", then "}
                    {discountText && discountText + ", then "}
                    {priceText}. Cancel anytime.</p>
*/}
            </div>
        </div>;
    }

    renderUpdateButton(urlParams, offer, subscription) {
        if (!offer || !subscription) return '';

        let buttonParams = urlParams;
        if (offer.offerId) {
            buttonParams = new URLSearchParams(urlParams);
            buttonParams.set("discount_offer", offer.offerId);
        }

        const alreadySubscribedToOffer = subscription.planId === offer.planId
        const active = ['TRIALING', 'ACTIVE'].includes(subscription.status) && !subscription.canceledAt;
        const cancelled = ['TRIALING', 'ACTIVE'].includes(subscription.status) && subscription.canceledAt;
        const incomplete = ['INCOMPLETE'].includes(subscription.status);
        const pastDue = ['PAST_DUE'].includes(subscription.status);

        const onClick = () => {
            if (alreadySubscribedToOffer && cancelled) {
                this.setState({showConfirmationDialog: true});
            } else if (pastDue) {
                this.fetchPortalUrl(portalSessionUrl => {
                    window.location.replace(portalSessionUrl);
                });
            } else if (incomplete) {
                this.props.history.push(`/account/subscribe/embedded-checkout?${buttonParams.toString()}`);
            } else if (alreadySubscribedToOffer && active) {
                this.props.history.push("/account/dashboard");
            } else {
                this.setState({showConfirmationDialog: true});
            }
        }

        return <div className="max-w-50rem mx-auto">
            <button className="btn btn-primary btn-sm w-100" onClick={onClick} disabled={this.state.manageInProgress === true}>{
                alreadySubscribedToOffer && active ? "Manage in account dashboard" :
                alreadySubscribedToOffer && cancelled ? "Reactivate" :
                incomplete ?
                "Retry Checkout" :
                    pastDue ?
                    <>
                        Finalize pending invoice
                        {this.state.manageInProgress && <div className="spinner-border spinner-border-sm ml-2" role="status">
                            <span className="sr-only">Loading...</span></div>}
                    </> :
                    "Change plan"}
            </button>
            <div className="pt-2">
                {alreadySubscribedToOffer && active ?
                    <p>Review your status or cancel your subscription.</p> :
                    incomplete ?
                    <p>Try again with the existing or a new payment method.</p> :
                    <p>You can review your new plan before finalizing the change.</p>
                }
            </div>
        </div>;
    }

    renderPlansLoading() {
        return <div className="d-lg-flex align-items-center justify-content-center">
            <Loader
                type="Rings"
                color="#1a6aff"
                secondaryColor="#1a6aff"
                height={100}
                width={100}
            />
        </div>
    }

    renderPlanOptions(urlParams, options, subscription) {
        const productId = productIdFromParams(urlParams);
        const intervalId = intervalIdFromParams(urlParams);
        const intervals = [INTERVAL_MONTH, INTERVAL_YEAR];

        const cheaperFirst = (o1, o2) => {
            const price1 = Math.min(o2.standardAmount, o2.offerAmount || 9999);
            const price2 = Math.min(o1.standardAmount, o1.offerAmount || 9999);
            return (price2 - price1);
        }

        const goMonthly = (event) => {
            const query = new URLSearchParams(this.props.location.search)
            query.set("interval", intervals[0].toLowerCase());
            const search = query.toString();

            this.props.history.push(`/account/subscribe/cart?${search}`);
        };

        const goYearly = (event) => {
            const query = new URLSearchParams(this.props.location.search)
            query.set("interval", intervals[1].toLowerCase());
            const search = query.toString();

            this.props.history.push(`/account/subscribe/cart?${search}`);
        };

        const plans = options
            .filter(offer => matchesCaseInsensitive(offer.billingCycle, intervalId))
            .toSorted(cheaperFirst);

        // Show a badge
        const warningBadge = <span className="fa ml-2 text-danger fa-exclamation-circle"/>;
        const monthlyWarning = subscription && (subscription.status === "INCOMPLETE" || subscription.status === "PAST_DUE") &&
            subscription.billingCycle.toUpperCase() === INTERVAL_MONTH && warningBadge;
        const yearlyWarning = subscription && (subscription.status === "INCOMPLETE" || subscription.status === "PAST_DUE") &&
            subscription.billingCycle.toUpperCase() === INTERVAL_YEAR && warningBadge;

        return <div className="mb-6 position-relative">
            <div className="d-flex justify-content-around mb-6">
                <ul className="nav nav-segment nav-pills" style={{backgroundColor: "#e8e8e8"}}>
                    <li className="nav-item">
                        <a className={"nav-link py-1 " + (intervalId === INTERVAL_MONTH ? "active" : "")}
                           role="button"
                           onClick={goMonthly}>Monthly{monthlyWarning}</a>
                    </li>
                    <li className="nav-item">
                        <a className={"nav-link py-1 " + (intervalId === INTERVAL_YEAR ? "active" : "")}
                           role="button"
                           onClick={goYearly}>Yearly{yearlyWarning}</a>
                    </li>
                </ul>
            </div>

            <OptionBar id="products" className="w-100 mx-auto"
                       activeIndex={plans.findIndex(plan => matchesCaseInsensitive(plan.productId,productId))}
                       onOptionSelected={(idx) => {
                           if (idx < 0 || idx >= plans.length) return;

                           // Update URL params & reload
                           const query = new URLSearchParams(this.props.location.search)
                           query.set("product", plans[idx].productId.toLowerCase());
                           const search = query.toString();

                           this.props.history.push(`/account/subscribe/cart?${search}`);
                       }}>
                {plans.map((plan,idx) => <PricingTier
                    key={`plan_option_${idx}`}
                    plan={plan}
                    subscription={subscription && plan && subscription.planId === plan.planId ? subscription : null}/> )}
            </OptionBar>
        </div>;
    }

    renderConfirmChangeDialog(selectedOffer) {
        const { subscription, showConfirmationDialog, updateSubscriptionInProgress } = this.state;
        if (!subscription || !subscription.planId) return;

        const alreadySubscribedToOffer = subscription.planId === selectedOffer.planId;
        const cancelled = ['TRIALING', 'ACTIVE'].includes(subscription.status) && subscription.canceledAt;

        const handleClose = () => this.setState({ showConfirmationDialog: false });
        const handleContinue = () => {
            this.setState({updateSubscriptionInProgress: true});
            this.updateSubscription(selectedOffer.productId, selectedOffer.billingCycle, selectedOffer.offerId,
                (subscription, error) => {
                    this.setState({updateSubscriptionInProgress: false, showConfirmationDialog: false});
                    if (subscription && !error) {
                        successToast(`Your subscription has been ${alreadySubscribedToOffer ? "reactivated!" : "updated"}`);
                    } else if (error) {
                        failedToast(`Your subscription could not be ${alreadySubscribedToOffer ? "reactivated!" : "updated"}. Please try again later.`);
                    }
                });
        };

        return <div>
            <div className={`modal-backdrop fade ${showConfirmationDialog ? "show d-block" : "d-none"}`}/>
            <div id="confirm_modal" className={`modal fade ${showConfirmationDialog ? "show d-block" : "d-none"}`} tabIndex="-1" role="dialog"
                 aria-labelledby="confirm_modal_label" aria-hidden={!showConfirmationDialog} aria-modal={showConfirmationDialog}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 id="confirm_modal_label" className="modal-title">Confirm plan update</h3>
                            <button type="button" className="close" onClick={handleClose}>
                                <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {alreadySubscribedToOffer ?
                                <p>We are ready to reactivate your current plan.</p> :
                                <p>We are ready to update your plan to <strong className="text-dark font-weight-bold">Fing {productPrettyName(selectedOffer.productId)}</strong>, billed every {selectedOffer.billingCycle}.</p>}
                            <p>{cancelled ?
                                "The change will reactivate your subscription and make it immediately available. " :
                                "The new plan will be immediately available. "}
                                Any charges or credits will be prorated accordingly and visible in your next invoice.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-sm btn-link" onClick={handleClose}>
                                Not now
                            </button>
                            <button className="btn btn-sm btn-primary" onClick={handleContinue} disabled={updateSubscriptionInProgress === true}>
                                {updateSubscriptionInProgress ?
                                    <>
                                        Updating
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </>:
                                    "Confirm"
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
    }}

export default withRouter(SubscribeCart)